import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';

import type { InvalidateDatasetInput } from '@infinex/main-service-contract/v2/client/user';
import type { DatasetKey } from '@infinex/types';

import { ConnectionDot } from './ConnectionDot';
import {
  DATA_FLASH_DURATION_MS,
  formatTimeAgo,
  INVALIDATION_FEEDBACK_MS,
} from './constants';
import { DataTreeView } from './DataTreeView';
import {
  CheckIcon,
  CloseIcon,
  CopyIcon,
  MenuIcon,
  RotateCwIcon,
} from './icons';

import { useTRPCClient } from '../../main-service-client';
import { datasetQueryKeys } from '../../provider/hooks/useDataset';
import { useUserDebug } from '../hooks';
import type {
  DatasetExtraTab,
  DatasetExtraTabs,
  DebugDatasetInfo,
  DebuggerData,
} from '../types';

// ═══════════════════════════════════════════════════════════════════════════════
// Dataset Row
// ═══════════════════════════════════════════════════════════════════════════════

const DEFAULT_INFO: DebugDatasetInfo = { status: 'idle', fetchStatus: 'idle' };

export function DatasetRow({
  datasetKey,
  info = DEFAULT_INFO,
  now,
  wsStatus,
  fallbackPollIntervalMs,
  extraTabs: extraTabsFn,
  isDragging,
  isReordering,
  onGrabStart,
  onGrabMove,
  onGrabEnd,
  onMarkUpdated,
}: {
  datasetKey: DatasetKey;
  info?: DebugDatasetInfo;
  now: number;
  wsStatus: DebuggerData['wsStatus'];
  fallbackPollIntervalMs: number | false;
  extraTabs?: DatasetExtraTabs;
  /** Whether THIS row is the one being dragged */
  isDragging: boolean;
  /** Whether ANY row is being dragged (for transition animation) */
  isReordering: boolean;
  onGrabStart: () => void;
  onGrabMove: (clientY: number) => void;
  onGrabEnd: () => void;
  onMarkUpdated: (key: DatasetKey) => void;
}) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [activeTab, setActiveTab] = useState('browse');
  const [dataSearch, setDataSearch] = useState('');

  // Resolve extra tabs for this dataset (e.g. Optimistic UI State for "user")
  const resolvedExtraTabs: DatasetExtraTab[] = extraTabsFn
    ? extraTabsFn({ datasetKey })
    : [];

  const apiClient = useTRPCClient();
  const queryClient = useQueryClient();
  const { logDebugMessage } = useUserDebug();

  // ── Invalidation with visual feedback ──
  const [feedbackState, setFeedbackState] = useState<
    'idle' | 'success' | 'error'
  >('idle');

  const isWsConnected = wsStatus === 'connected';

  // Invalidate: tells backend to rebuild, then WS pushes fresh data
  const invalidateMutation = useMutation({
    mutationFn: async () => {
      logDebugMessage({
        type: 'status',
        message: `Invalidating dataset: ${datasetKey}`,
      });
      const input: InvalidateDatasetInput = { key: datasetKey };
      await apiClient.user.invalidateDataset.mutate(input);
    },
    onSuccess: () => {
      setFeedbackState('success');
      setTimeout(() => setFeedbackState('idle'), INVALIDATION_FEEDBACK_MS);
      logDebugMessage({
        type: 'status',
        message: `Dataset invalidated: ${datasetKey}`,
      });
    },
    onError: (error) => {
      setFeedbackState('error');
      setTimeout(() => setFeedbackState('idle'), INVALIDATION_FEEDBACK_MS);
      logDebugMessage({
        type: 'error',
        message: `Failed to invalidate ${datasetKey}: ${error}`,
      });
    },
  });

  // Re-fetch: directly fetches via tRPC and injects into cache (no backend invalidation)
  const refetchMutation = useMutation({
    mutationFn: async () => {
      logDebugMessage({
        type: 'status',
        message: `Re-fetching dataset via tRPC: ${datasetKey}`,
      });
      const freshData = await apiClient.user.getDataset.query({
        key: datasetKey,
      });
      queryClient.setQueryData(datasetQueryKeys.dataset(datasetKey), freshData);
      onMarkUpdated(datasetKey);
    },
    onSuccess: () => {
      setFeedbackState('success');
      setTimeout(() => setFeedbackState('idle'), INVALIDATION_FEEDBACK_MS);
      logDebugMessage({
        type: 'status',
        message: `Dataset re-fetched: ${datasetKey}`,
      });
    },
    onError: (error) => {
      setFeedbackState('error');
      setTimeout(() => setFeedbackState('idle'), INVALIDATION_FEEDBACK_MS);
      logDebugMessage({
        type: 'error',
        message: `Failed to re-fetch ${datasetKey}: ${error}`,
      });
    },
  });

  const activeMutation = isWsConnected ? invalidateMutation : refetchMutation;

  // ── Green border flash when new data arrives ──
  const [isFlashing, setIsFlashing] = useState(false);
  const prevDataUpdatedAt = useRef(info.dataUpdatedAt);

  useEffect(() => {
    if (
      info.dataUpdatedAt !== undefined &&
      prevDataUpdatedAt.current !== undefined &&
      info.dataUpdatedAt !== prevDataUpdatedAt.current
    ) {
      setIsFlashing(true);
      const timer = setTimeout(
        () => setIsFlashing(false),
        DATA_FLASH_DURATION_MS
      );
      prevDataUpdatedAt.current = info.dataUpdatedAt;
      return () => clearTimeout(timer);
    }
    prevDataUpdatedAt.current = info.dataUpdatedAt;
  }, [info.dataUpdatedAt]);

  const timeAgo = formatTimeAgo(info.dataUpdatedAt, now);
  const hasData = info.status === 'loaded' || info.data !== undefined;

  return (
    <div
      data-dataset-key={datasetKey}
      className={`relative overflow-hidden rounded-lg border ${
        isDragging
          ? 'border-info z-20 scale-[1.02] shadow-lg transition-[transform,box-shadow] duration-150'
          : isReordering
            ? 'transition-[transform] duration-200'
            : 'transition-[border-color] duration-700'
      }`}
      style={
        isFlashing
          ? { borderColor: 'var(--border-color-positiveEmphasis)' }
          : undefined
      }
    >
      {/* ─── Row header ─── */}
      <div
        className={`flex w-full cursor-pointer items-center gap-2 px-3 py-1.5 transition-colors ${
          isExpanded ? 'bg-fill' : 'bg-surface hover:bg-surfaceHover'
        }`}
        onClick={() => setIsExpanded(!isExpanded)}
        role="button"
        tabIndex={-1}
      >
        {/* Connection-aware status dot with tooltip (outside button to avoid nesting) */}
        <ConnectionDot
          status={info.status}
          source={info.source}
          wsStatus={wsStatus}
          dataUpdatedAt={info.dataUpdatedAt}
          fallbackPollIntervalMs={fallbackPollIntervalMs}
          now={now}
        />

        {/* Name */}
        <span className="text-emphasis text-sm font-semibold">
          {datasetKey}
        </span>

        {/* Time ago or status label */}
        {timeAgo ? (
          <span className="text-tertiary text-xs">{timeAgo}</span>
        ) : (
          <span className="text-tertiary text-xs italic">{info.status}</span>
        )}

        {/* Spacer */}
        <span className="flex-1" />

        {/* Invalidate / Re-fetch button */}
        <ActionButton
          isPending={activeMutation.isPending}
          feedbackState={feedbackState}
          label={isWsConnected ? 'Invalidate' : 'Re-Fetch'}
          onAction={(e) => {
            e.stopPropagation();
            activeMutation.mutate();
          }}
        />

        {/* Drag handle */}
        <span
          className="text-tertiary hover:text-secondary cursor-grab active:cursor-grabbing"
          onMouseDown={(e) => {
            e.stopPropagation();
            e.preventDefault();
            onGrabStart();

            const onMove = (ev: MouseEvent) => onGrabMove(ev.clientY);
            const onUp = () => {
              onGrabEnd();
              document.removeEventListener('mousemove', onMove);
              document.removeEventListener('mouseup', onUp);
              document.body.style.cursor = '';
              document.body.style.userSelect = '';
            };
            document.addEventListener('mousemove', onMove);
            document.addEventListener('mouseup', onUp);
            document.body.style.cursor = 'grabbing';
            document.body.style.userSelect = 'none';
          }}
          onClick={(e) => e.stopPropagation()}
          title="Drag to reorder"
        >
          <MenuIcon size={12} />
        </span>
      </div>

      {/* ─── Expanded content ─── */}
      {isExpanded ? (
        <div className="border-t">
          {/* Tab bar */}
          <div className="flex border-b">
            <TabButton
              isActive={activeTab === 'browse'}
              onClick={() => setActiveTab('browse')}
              label="Browse"
            />
            <TabButton
              isActive={activeTab === 'raw'}
              onClick={() => setActiveTab('raw')}
              label="Raw"
            />
            {resolvedExtraTabs.map((tab) => (
              <TabButton
                key={tab.label}
                isActive={activeTab === tab.label}
                onClick={() => setActiveTab(tab.label)}
                label={tab.label}
              />
            ))}
          </div>

          {/* Built-in tabs */}
          {activeTab === 'browse' ? (
            hasData ? (
              <BrowseTab
                data={info.data}
                searchQuery={dataSearch}
                onSearchChange={setDataSearch}
              />
            ) : (
              <p className="text-tertiary px-3 py-4 text-center text-xs">
                No data available
              </p>
            )
          ) : activeTab === 'raw' ? (
            hasData ? (
              <RawTab data={info.data} />
            ) : (
              <p className="text-tertiary px-3 py-4 text-center text-xs">
                No data available
              </p>
            )
          ) : (
            // Extra tab content
            (resolvedExtraTabs.find((t) => t.label === activeTab)?.content ??
            null)
          )}
        </div>
      ) : null}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// Sub-components
// ═══════════════════════════════════════════════════════════════════════════════

function ActionButton({
  isPending,
  feedbackState,
  label,
  onAction,
}: {
  isPending: boolean;
  feedbackState: 'idle' | 'success' | 'error';
  label: string;
  onAction: (e: React.MouseEvent) => void;
}) {
  if (isPending) {
    return (
      <span className="text-secondary flex items-center gap-1.5 rounded border px-2 py-0.5 text-[10px] font-semibold">
        <RotateCwIcon size={12} className="animate-spin" />
        {label === 'Invalidate' ? 'Invalidating…' : 'Fetching…'}
      </span>
    );
  }

  if (feedbackState === 'success') {
    return (
      <span className="text-positive border-positive flex items-center gap-1 rounded border px-2 py-0.5 text-[10px] font-semibold">
        <CheckIcon size={12} />
        Done
      </span>
    );
  }

  if (feedbackState === 'error') {
    return (
      <span className="text-critical border-critical flex items-center gap-1 rounded border px-2 py-0.5 text-[10px] font-semibold">
        <CloseIcon size={12} />
        Failed
      </span>
    );
  }

  return (
    <button
      className="text-secondary hover:text-emphasis hover:bg-fillHover active:bg-fillPress flex cursor-pointer items-center gap-1 rounded border px-2 py-0.5 text-[10px] font-semibold transition-colors"
      onClick={onAction}
    >
      <RotateCwIcon size={12} />
      {label}
    </button>
  );
}

function TabButton({
  isActive,
  onClick,
  label,
}: {
  isActive: boolean;
  onClick: () => void;
  label: string;
}) {
  return (
    <button
      className={`cursor-pointer px-3 py-1 text-xs font-semibold transition-colors ${
        isActive
          ? 'text-emphasis border-info border-b-2'
          : 'text-secondary hover:text-default'
      }`}
      onClick={(e) => {
        e.stopPropagation();
        onClick();
      }}
    >
      {label}
    </button>
  );
}

function BrowseTab({
  data,
  searchQuery,
  onSearchChange,
}: {
  data: unknown;
  searchQuery: string;
  onSearchChange: (q: string) => void;
}) {
  const scrollRef = useRef<HTMLDivElement>(null);

  // Scroll to first search match after the tree re-renders with expanded nodes
  useEffect(() => {
    if (!searchQuery || !scrollRef.current) return;
    const timer = setTimeout(() => {
      const firstMatch = scrollRef.current?.querySelector(
        '[data-search-match]'
      );
      if (firstMatch) {
        firstMatch.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }
    }, 50);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  return (
    <div className="flex flex-col">
      <div className="border-b px-3 py-1.5">
        <input
          type="text"
          placeholder="Search in dataset…"
          value={searchQuery}
          onChange={(e) => onSearchChange(e.target.value)}
          className="bg-fill text-default placeholder:text-tertiary focus:border-info w-full rounded border px-2 py-1 font-mono text-xs transition-colors outline-none"
        />
      </div>
      <div
        ref={scrollRef}
        className="max-h-[60vh] overflow-auto overscroll-contain px-3 py-1.5"
      >
        <DataTreeView data={data} searchQuery={searchQuery} />
      </div>
    </div>
  );
}

function RawTab({ data }: { data: unknown }) {
  const [hasCopied, setHasCopied] = useState(false);
  const jsonText = JSON.stringify(data, null, 2);

  const handleCopy = () => {
    void navigator.clipboard.writeText(jsonText);
    setHasCopied(true);
    setTimeout(() => setHasCopied(false), 1500);
  };

  return (
    <div className="relative max-h-[60vh] overflow-auto overscroll-contain">
      <button
        className="bg-surface hover:bg-surfaceHover text-secondary absolute top-2 right-2 flex cursor-pointer items-center gap-1 rounded border px-2 py-0.5 text-[10px] font-semibold transition-colors"
        onClick={handleCopy}
      >
        {hasCopied ? (
          <>
            <CheckIcon size={12} />
            Copied
          </>
        ) : (
          <>
            <CopyIcon size={12} />
            Copy
          </>
        )}
      </button>
      <pre className="text-default px-3 py-1.5 font-mono text-xs">
        {jsonText}
      </pre>
    </div>
  );
}
